// Draws assessment figures from the structured descriptions the AI returns (validated on the
// server by api/ai/_lib/assessment_figures.php). Output is a print-friendly black and white
// SVG string, used for the on-screen preview and embedded in the Word document.

import { compileExpression } from './figureExpression';
import { FIGURE_TEMPLATES } from './figureTemplates';

const FONT = 'font-family="Arial, Helvetica, sans-serif"';
const FILLS = ['#4d4d4d', '#b3b3b3', 'url(#fig-hatch)', '#ffffff'];

export function escapeSvg(value) {
    return String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
}

// South African papers use a decimal comma and a true minus sign.
export function formatFigureNumber(value) {
    const rounded = Math.round(Number(value) * 1000) / 1000;
    const text = String(Math.abs(rounded)).replace('.', ',');
    return rounded < 0 ? `−${text}` : text;
}

function text(x, y, content, { anchor = 'middle', size = 13, weight = 'normal', baseline = 'middle', italic = false } = {}) {
    return `<text x="${x.toFixed(1)}" y="${y.toFixed(1)}" ${FONT} font-size="${size}" font-weight="${weight}"${italic ? ' font-style="italic"' : ''} text-anchor="${anchor}" dominant-baseline="${baseline}" fill="#000">${escapeSvg(content)}</text>`;
}

// `extra` attributes override the defaults; SVG parsers used by Word reject duplicate attributes.
function line(x1, y1, x2, y2, extra = '') {
    const attributes = { stroke: '#000', 'stroke-width': '1.4' };
    for (const [, name, value] of extra.matchAll(/([\w-]+)="([^"]*)"/g)) attributes[name] = value;
    const rendered = Object.entries(attributes).map(([name, value]) => `${name}="${value}"`).join(' ');
    return `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" ${rendered}/>`;
}

function arrowHead(x, y, angle, size = 9) {
    const left = angle + Math.PI * 0.85;
    const right = angle - Math.PI * 0.85;
    return `<path d="M${x.toFixed(1)} ${y.toFixed(1)} L${(x + size * Math.cos(left)).toFixed(1)} ${(y + size * Math.sin(left)).toFixed(1)} L${(x + size * Math.cos(right)).toFixed(1)} ${(y + size * Math.sin(right)).toFixed(1)} Z" fill="#000"/>`;
}

function wrap(width, height, body) {
    return {
        width: Math.round(width),
        height: Math.round(height),
        svg: `<svg xmlns="http://www.w3.org/2000/svg" width="${Math.round(width)}" height="${Math.round(height)}" viewBox="0 0 ${Math.round(width)} ${Math.round(height)}">`
            + `<defs><pattern id="fig-hatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="8" height="8" fill="#fff"/><line x1="0" y1="0" x2="0" y2="8" stroke="#000" stroke-width="1.6"/></pattern></defs>`
            + `<rect width="100%" height="100%" fill="#fff"/>${body}</svg>`
    };
}

// A tick step of 1, 2 or 5 x 10^n giving at most `maxTicks` intervals.
function niceStep(range, maxTicks = 10) {
    const rough = range / maxTicks;
    const magnitude = 10 ** Math.floor(Math.log10(rough));
    return [1, 2, 5, 10].map((factor) => factor * magnitude).find((step) => step >= rough) || rough;
}

function ticks(min, max, step) {
    const values = [];
    for (let value = Math.ceil(min / step - 1e-9) * step; value <= max + step * 1e-6; value += step) {
        values.push(Math.round(value / step) * step);
    }
    return values;
}

function numberLine(figure) {
    const width = 580;
    const height = 110;
    const axisY = 62;
    const left = 30;
    const right = width - 30;
    const scale = (value) => left + ((value - figure.min) / (figure.max - figure.min)) * (right - left);
    const tickValues = ticks(figure.min, figure.max, figure.step);
    const spacing = (right - left) / Math.max(1, tickValues.length - 1);
    const labelEvery = spacing >= 26 ? 1 : spacing >= 13 ? 2 : 5;
    let body = line(left - 18, axisY, right + 18, axisY) + arrowHead(right + 20, axisY, 0) + arrowHead(left - 20, axisY, Math.PI);

    tickValues.forEach((value, index) => {
        const x = scale(value);
        body += line(x, axisY - 7, x, axisY + 7);
        if (index % labelEvery === 0) body += text(x, axisY + 22, formatFigureNumber(value), { size: 12 });
    });

    const dot = (value, open) => `<circle cx="${scale(value).toFixed(1)}" cy="${axisY}" r="6" fill="${open ? '#fff' : '#000'}" stroke="#000" stroke-width="2"/>`;
    for (const interval of figure.intervals || []) {
        const hasFrom = typeof interval.from === 'number';
        const hasTo = typeof interval.to === 'number';
        const start = hasFrom ? scale(interval.from) : left - 14;
        const end = hasTo ? scale(interval.to) : right + 14;
        body += `<line x1="${start.toFixed(1)}" y1="${axisY - 14}" x2="${end.toFixed(1)}" y2="${axisY - 14}" stroke="#000" stroke-width="4"/>`;
        if (!hasFrom) body += arrowHead(start - 4, axisY - 14, Math.PI, 11);
        if (!hasTo) body += arrowHead(end + 4, axisY - 14, 0, 11);
        if (hasFrom) body += line(start, axisY - 14, start, axisY) + dot(interval.from, interval.fromOpen);
        if (hasTo) body += line(end, axisY - 14, end, axisY) + dot(interval.to, interval.toOpen);
    }
    for (const point of figure.points || []) {
        body += dot(point.x, point.open);
        if (point.label) body += text(scale(point.x), axisY - 28, point.label, { weight: 'bold' });
    }
    return wrap(width, height, body);
}

// Fits all points of the given shapes into a box, keeping the aspect ratio (y points up).
function fitter(points, boxWidth, boxHeight, padding) {
    const xs = points.map((point) => point.x);
    const ys = points.map((point) => point.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const spanX = Math.max(1e-6, Math.max(...xs) - minX);
    const spanY = Math.max(1e-6, Math.max(...ys) - minY);
    const scale = Math.min((boxWidth - 2 * padding) / spanX, (boxHeight - 2 * padding) / spanY);
    const offsetX = (boxWidth - spanX * scale) / 2;
    const offsetY = (boxHeight - spanY * scale) / 2;
    return (point) => ({ x: offsetX + (point.x - minX) * scale, y: boxHeight - offsetY - (point.y - minY) * scale });
}

function unit(dx, dy) {
    const length = Math.hypot(dx, dy) || 1;
    return [dx / length, dy / length];
}

function geometry(figure, { notToScale = 'Not drawn to scale' } = {}) {
    const width = 460;
    const height = 330;
    const all = figure.shapes.flatMap((shape) => shape.points);
    const place = fitter(all, width, height - 20, 54);
    let body = '';
    let labelled = false;

    for (const shape of figure.shapes) {
        const points = shape.points.map(place);
        const centroid = points.reduce((sum, point) => ({ x: sum.x + point.x / points.length, y: sum.y + point.y / points.length }), { x: 0, y: 0 });
        const closed = points.length >= 3;
        body += closed
            ? `<polygon points="${points.map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ')}" fill="none" stroke="#000" stroke-width="2"/>`
            : line(points[0].x, points[0].y, points[1].x, points[1].y, 'stroke-width="2"');

        for (const [index, source] of shape.points.entries()) {
            const point = points[index];
            const previous = points[(index - 1 + points.length) % points.length];
            const following = points[(index + 1) % points.length];
            const [outX, outY] = unit(point.x - centroid.x, point.y - centroid.y);
            if (source.label) body += text(point.x + outX * 18, point.y + outY * 18, source.label, { weight: 'bold', size: 14 });

            if (closed) {
                const [ax, ay] = unit(previous.x - point.x, previous.y - point.y);
                const [bx, by] = unit(following.x - point.x, following.y - point.y);
                if (source.rightAngle) {
                    const size = 13;
                    body += `<polyline points="${(point.x + ax * size).toFixed(1)},${(point.y + ay * size).toFixed(1)} ${(point.x + (ax + bx) * size).toFixed(1)},${(point.y + (ay + by) * size).toFixed(1)} ${(point.x + bx * size).toFixed(1)},${(point.y + by * size).toFixed(1)}" fill="none" stroke="#000" stroke-width="1.3"/>`;
                } else if (source.angleLabel) {
                    // Arc on the interior side: sweep from one edge to the other through the bisector.
                    const start = Math.atan2(ay, ax);
                    let sweep = Math.atan2(by, bx) - start;
                    while (sweep <= -Math.PI) sweep += 2 * Math.PI;
                    while (sweep > Math.PI) sweep -= 2 * Math.PI;
                    const arc = Array.from({ length: 13 }, (_, step) => {
                        const angle = start + (sweep * step) / 12;
                        return `${(point.x + 20 * Math.cos(angle)).toFixed(1)},${(point.y + 20 * Math.sin(angle)).toFixed(1)}`;
                    });
                    body += `<polyline points="${arc.join(' ')}" fill="none" stroke="#000" stroke-width="1.2"/>`;
                }
                if (source.angleLabel) {
                    const [mx, my] = unit(ax + bx, ay + by);
                    body += text(point.x + mx * 36, point.y + my * 36, source.angleLabel, { size: 12 });
                    labelled = true;
                }
            }

            if (source.sideLabel && (closed || index === 0)) {
                const middle = { x: (point.x + following.x) / 2, y: (point.y + following.y) / 2 };
                const [nx, ny] = unit(middle.x - centroid.x, middle.y - centroid.y);
                body += text(middle.x + nx * 16, middle.y + ny * 16, source.sideLabel, { size: 12 });
                labelled = true;
            }
        }
    }

    if (labelled) body += text(width - 8, height - 10, notToScale, { anchor: 'end', size: 10, italic: true });
    return wrap(width, height, body);
}

function grid(figure) {
    const columns = Math.round(figure.xMax);
    const rows = Math.round(figure.yMax);
    const cell = Math.min(30, 480 / columns, 360 / rows);
    const left = 34;
    const top = 14;
    const width = left + columns * cell + 16;
    const height = top + rows * cell + 30;
    const toX = (x) => left + x * cell;
    const toY = (y) => top + (rows - y) * cell;
    let body = '';
    for (let x = 0; x <= columns; x += 1) body += line(toX(x), toY(0), toX(x), toY(rows), 'stroke="#9a9a9a" stroke-width="0.8"');
    for (let y = 0; y <= rows; y += 1) body += line(toX(0), toY(y), toX(columns), toY(y), 'stroke="#9a9a9a" stroke-width="0.8"');
    const labelEvery = cell >= 18 ? 1 : 2;
    for (let x = 0; x <= columns; x += labelEvery) body += text(toX(x), toY(0) + 14, formatFigureNumber(x), { size: 10 });
    for (let y = labelEvery; y <= rows; y += labelEvery) body += text(toX(0) - 10, toY(y), formatFigureNumber(y), { size: 10, anchor: 'end' });

    figure.shapes.forEach((shape, index) => {
        const points = shape.points.map((point) => ({ ...point, sx: toX(point.x), sy: toY(point.y) }));
        const fill = index === 0 ? '#c8c8c8' : index === 1 ? 'url(#fig-hatch)' : '#ffffff';
        const dash = index >= 2 ? ' stroke-dasharray="6 4"' : '';
        body += `<polygon points="${points.map((point) => `${point.sx.toFixed(1)},${point.sy.toFixed(1)}`).join(' ')}" fill="${fill}" fill-opacity="0.85" stroke="#000" stroke-width="2"${dash}/>`;
        const centre = points.reduce((sum, point) => ({ x: sum.x + point.sx / points.length, y: sum.y + point.sy / points.length }), { x: 0, y: 0 });
        if (shape.label) body += `<rect x="${(centre.x - 9).toFixed(1)}" y="${(centre.y - 9).toFixed(1)}" width="18" height="18" fill="#fff" stroke="none"/>` + text(centre.x, centre.y, shape.label, { weight: 'bold', size: 13 });
        for (const point of points) {
            if (!point.label) continue;
            const [dx, dy] = unit(point.sx - centre.x, point.sy - centre.y);
            body += text(point.sx + dx * 12, point.sy + dy * 12, point.label, { size: 11, weight: 'bold' });
        }
    });
    return wrap(width, height, body);
}

function axesFrame({ xMin, xMax, yMin, yMax, width, height, margin }) {
    const plotWidth = width - margin.left - margin.right;
    const plotHeight = height - margin.top - margin.bottom;
    return {
        toX: (x) => margin.left + ((x - xMin) / (xMax - xMin)) * plotWidth,
        toY: (y) => margin.top + plotHeight - ((y - yMin) / (yMax - yMin)) * plotHeight,
        plotWidth,
        plotHeight
    };
}

function cartesianGraph(figure) {
    const width = 500;
    const height = 420;
    const margin = { left: 30, right: 30, top: 24, bottom: 30 };
    const { xMin, xMax, yMin, yMax } = figure;
    const { toX, toY, plotWidth, plotHeight } = axesFrame({ xMin, xMax, yMin, yMax, width, height, margin });
    const axisX = toY(Math.min(Math.max(0, yMin), yMax));
    const axisY = toX(Math.min(Math.max(0, xMin), xMax));
    const xStep = niceStep(xMax - xMin, 10);
    const yStep = niceStep(yMax - yMin, 10);
    let body = `<clipPath id="fig-plot"><rect x="${margin.left}" y="${margin.top}" width="${plotWidth}" height="${plotHeight}"/></clipPath>`;

    for (const x of ticks(xMin, xMax, xStep)) {
        body += line(toX(x), margin.top, toX(x), margin.top + plotHeight, 'stroke="#d9d9d9" stroke-width="0.8"');
        if (Math.abs(x) > 1e-9) body += line(toX(x), axisX - 4, toX(x), axisX + 4) + text(toX(x), axisX + 14, formatFigureNumber(x), { size: 10 });
    }
    for (const y of ticks(yMin, yMax, yStep)) {
        body += line(margin.left, toY(y), margin.left + plotWidth, toY(y), 'stroke="#d9d9d9" stroke-width="0.8"');
        if (Math.abs(y) > 1e-9) body += line(axisY - 4, toY(y), axisY + 4, toY(y)) + text(axisY - 7, toY(y), formatFigureNumber(y), { size: 10, anchor: 'end' });
    }
    body += line(margin.left - 8, axisX, margin.left + plotWidth + 10, axisX) + arrowHead(margin.left + plotWidth + 12, axisX, 0);
    body += line(axisY, margin.top + plotHeight + 8, axisY, margin.top - 10) + arrowHead(axisY, margin.top - 12, -Math.PI / 2);
    body += text(margin.left + plotWidth + 14, axisX - 12, figure.xAxisLabel || 'x', { italic: true, anchor: 'end' });
    body += text(axisY + 10, margin.top - 8, figure.yAxisLabel || 'y', { italic: true, anchor: 'start' });
    if (xMin <= 0 && xMax >= 0 && yMin <= 0 && yMax >= 0) body += text(axisY - 8, axisX + 12, 'O', { size: 11, italic: true });

    let curves = '';
    for (const fn of figure.functions || []) {
        const evaluate = compileExpression(fn.expression);
        const samples = 480;
        const segments = [];
        let current = [];
        let previousY = null;
        for (let step = 0; step <= samples; step += 1) {
            const x = xMin + ((xMax - xMin) * step) / samples;
            let y;
            try { y = evaluate(x); } catch { y = NaN; }
            // Break the curve at gaps and asymptotes instead of joining across them.
            const drawable = Number.isFinite(y) && Math.abs(y) <= (yMax - yMin) * 50;
            const jump = drawable && previousY !== null && Math.abs(y - previousY) > (yMax - yMin) * 1.5;
            if (!drawable || jump) {
                if (current.length > 1) segments.push(current);
                current = [];
            }
            previousY = drawable ? y : null;
            if (drawable) current.push([toX(x), toY(y)]);
        }
        if (current.length > 1) segments.push(current);
        curves += segments.map((segment) => `<polyline points="${segment.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')}" fill="none" stroke="#000" stroke-width="2"/>`).join('');

        // Label the curve near the right-most visible point.
        const visible = segments.flat().filter(([x, y]) => y >= margin.top + 8 && y <= margin.top + plotHeight - 8 && x <= margin.left + plotWidth - 10);
        const anchor = visible[visible.length - 1];
        if (fn.label && anchor) body += text(anchor[0] - 6, anchor[1] - 12, fn.label, { italic: true, weight: 'bold', anchor: 'end' });
    }
    body += `<g clip-path="url(#fig-plot)">${curves}</g>`;

    for (const point of figure.points || []) {
        if (point.x < xMin || point.x > xMax || point.y < yMin || point.y > yMax) continue;
        body += `<circle cx="${toX(point.x).toFixed(1)}" cy="${toY(point.y).toFixed(1)}" r="4" fill="#000"/>`;
        if (point.label) body += text(toX(point.x) + 8, toY(point.y) - 10, point.label, { anchor: 'start', size: 12, weight: 'bold' });
    }
    return wrap(width, height, body);
}

function categoryChart(figure) {
    const isLine = figure.type === 'line_graph';
    const series = figure.series;
    const categories = figure.categories;
    const values = series.flatMap((entry) => entry.values);
    const dataMin = Math.min(0, ...values);
    const dataMax = Math.max(...values);
    const step = niceStep((dataMax - dataMin) || 1, 8);
    const yMin = Math.floor(dataMin / step) * step;
    const yMax = Math.ceil(dataMax / step) * step + (dataMax % step === 0 ? step : 0);
    const longest = Math.max(...categories.map((category) => String(category).length));
    const rotate = categories.length > 6 || longest > 10;
    const width = 560;
    const legendHeight = series.length > 1 ? 26 : 0;
    const margin = { left: 64, right: 18, top: 18 + legendHeight, bottom: rotate ? 92 : 58 };
    const height = 300 + margin.top + margin.bottom;
    const { toX, toY, plotWidth, plotHeight } = axesFrame({ xMin: 0, xMax: categories.length, yMin, yMax, width, height, margin });
    let body = '';

    for (const value of ticks(yMin, yMax, step)) {
        body += line(margin.left, toY(value), margin.left + plotWidth, toY(value), 'stroke="#d0d0d0" stroke-width="0.8"');
        body += text(margin.left - 8, toY(value), formatFigureNumber(value), { size: 11, anchor: 'end' });
    }
    const groupWidth = plotWidth / categories.length;
    categories.forEach((category, index) => {
        const centre = toX(index + 0.5);
        const label = String(category);
        body += rotate
            ? `<text x="${centre.toFixed(1)}" y="${(margin.top + plotHeight + 10).toFixed(1)}" ${FONT} font-size="11" text-anchor="end" transform="rotate(-40 ${centre.toFixed(1)} ${(margin.top + plotHeight + 10).toFixed(1)})">${escapeSvg(label)}</text>`
            : text(centre, margin.top + plotHeight + 16, label, { size: 11 });
    });

    if (isLine) {
        const markers = [
            (x, y) => `<circle cx="${x}" cy="${y}" r="4.5" fill="#000"/>`,
            (x, y) => `<rect x="${x - 4.5}" y="${y - 4.5}" width="9" height="9" fill="#fff" stroke="#000" stroke-width="1.6"/>`,
            (x, y) => `<path d="M${x} ${y - 6} L${x + 6} ${y + 4.5} L${x - 6} ${y + 4.5} Z" fill="#000"/>`,
            (x, y) => `<path d="M${x} ${y - 6} L${x + 6} ${y} L${x} ${y + 6} L${x - 6} ${y} Z" fill="#fff" stroke="#000" stroke-width="1.6"/>`
        ];
        series.forEach((entry, seriesIndex) => {
            const points = entry.values.map((value, index) => [toX(index + 0.5), toY(value)]);
            body += `<polyline points="${points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')}" fill="none" stroke="#000" stroke-width="1.8"${seriesIndex % 2 ? ' stroke-dasharray="7 4"' : ''}/>`;
            body += points.map(([x, y]) => markers[seriesIndex % markers.length](x.toFixed(1), y.toFixed(1))).join('');
        });
    } else {
        const barWidth = Math.min(46, (groupWidth * 0.72) / series.length);
        series.forEach((entry, seriesIndex) => {
            entry.values.forEach((value, index) => {
                const x = toX(index + 0.5) - (barWidth * series.length) / 2 + seriesIndex * barWidth;
                const top = toY(Math.max(0, value));
                const bottom = toY(Math.min(0, value));
                body += `<rect x="${x.toFixed(1)}" y="${top.toFixed(1)}" width="${barWidth.toFixed(1)}" height="${Math.max(0, bottom - top).toFixed(1)}" fill="${FILLS[seriesIndex % FILLS.length]}" stroke="#000" stroke-width="1.2"/>`;
            });
        });
    }

    body += line(margin.left, margin.top, margin.left, margin.top + plotHeight, 'stroke-width="1.6"');
    body += line(margin.left, toY(Math.max(0, yMin)), margin.left + plotWidth, toY(Math.max(0, yMin)), 'stroke-width="1.6"');
    if (figure.yAxisLabel) body += `<text x="16" y="${(margin.top + plotHeight / 2).toFixed(1)}" ${FONT} font-size="12" font-weight="bold" text-anchor="middle" transform="rotate(-90 16 ${(margin.top + plotHeight / 2).toFixed(1)})">${escapeSvg(figure.yAxisLabel)}</text>`;
    if (figure.xAxisLabel) body += text(margin.left + plotWidth / 2, height - 14, figure.xAxisLabel, { size: 12, weight: 'bold' });

    if (series.length > 1) {
        let x = margin.left;
        series.forEach((entry, index) => {
            const swatch = isLine
                ? line(x, 14, x + 22, 14, index % 2 ? 'stroke-width="1.8" stroke-dasharray="7 4"' : 'stroke-width="1.8"')
                : `<rect x="${x}" y="7" width="16" height="14" fill="${FILLS[index % FILLS.length]}" stroke="#000"/>`;
            body += swatch + text(x + 28, 14, entry.name, { anchor: 'start', size: 11 });
            x += 40 + String(entry.name).length * 6.5;
        });
    }
    return wrap(width, height, body);
}

function pieChart(figure) {
    const values = figure.series[0].values;
    const total = values.reduce((sum, value) => sum + value, 0);
    const width = 520;
    const height = 340;
    const cx = 260;
    const cy = 170;
    const radius = 118;
    const fills = ['#4d4d4d', '#ffffff', '#b3b3b3', 'url(#fig-hatch)', '#e3e3e3', '#808080'];
    let angle = -Math.PI / 2;
    let body = '';
    let labels = '';
    values.forEach((value, index) => {
        const sweep = (value / total) * 2 * Math.PI;
        if (sweep <= 0) return;
        const end = angle + sweep;
        const point = (theta, r = radius) => [cx + r * Math.cos(theta), cy + r * Math.sin(theta)];
        const [x1, y1] = point(angle);
        const [x2, y2] = point(end);
        body += sweep >= 2 * Math.PI - 1e-6
            ? `<circle cx="${cx}" cy="${cy}" r="${radius}" fill="${fills[index % fills.length]}" stroke="#000" stroke-width="1.4"/>`
            : `<path d="M${cx} ${cy} L${x1.toFixed(1)} ${y1.toFixed(1)} A${radius} ${radius} 0 ${sweep > Math.PI ? 1 : 0} 1 ${x2.toFixed(1)} ${y2.toFixed(1)} Z" fill="${fills[index % fills.length]}" stroke="#000" stroke-width="1.4"/>`;
        const middle = angle + sweep / 2;
        const [ex, ey] = point(middle, radius + 6);
        const [lx, ly] = point(middle, radius + 26);
        labels += line(...point(middle, radius - 10), ex, ey, 'stroke-width="1"') + line(ex, ey, lx, ly, 'stroke-width="1"');
        labels += text(lx + (Math.cos(middle) >= 0 ? 4 : -4), ly, figure.categories[index], { anchor: Math.cos(middle) >= 0 ? 'start' : 'end', size: 12 });
        angle = end;
    });
    return wrap(width, height, body + labels);
}

function fractionBars(figure) {
    const labelWidth = figure.bars.some((bar) => bar.label) ? 90 : 0;
    const barWidth = 440;
    const width = labelWidth + barWidth + 30;
    const height = figure.bars.length * 54 + 16;
    let body = '';
    figure.bars.forEach((bar, index) => {
        const y = 16 + index * 54;
        const x = labelWidth + 14;
        const part = barWidth / bar.parts;
        if (bar.label) body += text(labelWidth, y + 17, bar.label, { anchor: 'end', size: 13, weight: 'bold' });
        for (let piece = 0; piece < bar.parts; piece += 1) {
            body += `<rect x="${(x + piece * part).toFixed(1)}" y="${y}" width="${part.toFixed(1)}" height="34" fill="${piece < bar.shaded ? '#a6a6a6' : '#fff'}" stroke="#000" stroke-width="1.4"/>`;
        }
    });
    return wrap(width, height, body);
}

function clock(figure) {
    const size = 280;
    const c = size / 2;
    const r = 118;
    let body = `<circle cx="${c}" cy="${c}" r="${r}" fill="#fff" stroke="#000" stroke-width="3"/>`;
    for (let minute = 0; minute < 60; minute += 1) {
        const theta = (minute / 60) * 2 * Math.PI - Math.PI / 2;
        const inner = minute % 5 === 0 ? r - 13 : r - 6;
        body += line(c + inner * Math.cos(theta), c + inner * Math.sin(theta), c + r * Math.cos(theta), c + r * Math.sin(theta), `stroke-width="${minute % 5 === 0 ? 2.4 : 1}"`);
    }
    for (let hour = 1; hour <= 12; hour += 1) {
        const theta = (hour / 12) * 2 * Math.PI - Math.PI / 2;
        body += text(c + (r - 30) * Math.cos(theta), c + (r - 30) * Math.sin(theta), String(hour), { size: 17, weight: 'bold' });
    }
    const hand = (fraction, length, thickness) => {
        const theta = fraction * 2 * Math.PI - Math.PI / 2;
        return `<line x1="${c}" y1="${c}" x2="${(c + length * Math.cos(theta)).toFixed(1)}" y2="${(c + length * Math.sin(theta)).toFixed(1)}" stroke="#000" stroke-width="${thickness}" stroke-linecap="round"/>`;
    };
    body += hand(((figure.hours % 12) + figure.minutes / 60) / 12, r * 0.52, 7);
    body += hand(figure.minutes / 60, r * 0.8, 4);
    body += `<circle cx="${c}" cy="${c}" r="6" fill="#000"/>`;
    return wrap(size, size, body);
}

// Circuit symbols drawn along a wire from (x1, y1) to (x2, y2), centred on the wire.
function circuitSymbol(component, x, y, vertical) {
    const along = (offset) => (vertical ? [x, y + offset] : [x + offset, y]);
    const across = (offset, distance) => (vertical ? [x + distance, y + offset] : [x + offset, y + distance]);
    const blank = vertical
        ? `<rect x="${x - 8}" y="${y - 20}" width="16" height="40" fill="#fff"/>`
        : `<rect x="${x - 20}" y="${y - 8}" width="40" height="16" fill="#fff"/>`;
    const plate = (offset, half, width) => {
        const [ax, ay] = across(offset, -half);
        const [bx, by] = across(offset, half);
        return `<line x1="${ax}" y1="${ay}" x2="${bx}" y2="${by}" stroke="#000" stroke-width="${width}"/>`;
    };
    const lead = (from, to) => {
        const [ax, ay] = along(from);
        const [bx, by] = along(to);
        return `<line x1="${ax}" y1="${ay}" x2="${bx}" y2="${by}" stroke="#000" stroke-width="1.6"/>`;
    };
    const meter = (letter) => `<circle cx="${x}" cy="${y}" r="13" fill="#fff" stroke="#000" stroke-width="1.6"/>` + text(x, y + 1, letter, { size: 13, weight: 'bold' });

    switch (component.kind) {
        case 'cell':
            return blank + lead(-20, -4) + lead(4, 20) + plate(-4, 16, 1.8) + plate(4, 8, 4.5);
        case 'battery':
            return blank + lead(-20, -10) + lead(10, 20) + plate(-10, 16, 1.8) + plate(-3, 8, 4.5) + plate(4, 16, 1.8) + plate(11, 8, 4.5);
        case 'bulb': {
            const d = 9;
            return blank + lead(-20, -13) + lead(13, 20) + `<circle cx="${x}" cy="${y}" r="13" fill="#fff" stroke="#000" stroke-width="1.6"/>`
                + `<line x1="${x - d}" y1="${y - d}" x2="${x + d}" y2="${y + d}" stroke="#000" stroke-width="1.4"/><line x1="${x - d}" y1="${y + d}" x2="${x + d}" y2="${y - d}" stroke="#000" stroke-width="1.4"/>`;
        }
        case 'resistor':
            return blank + lead(-20, -15) + lead(15, 20) + (vertical
                ? `<rect x="${x - 7}" y="${y - 15}" width="14" height="30" fill="#fff" stroke="#000" stroke-width="1.6"/>`
                : `<rect x="${x - 15}" y="${y - 7}" width="30" height="14" fill="#fff" stroke="#000" stroke-width="1.6"/>`);
        case 'switch_open':
        case 'switch_closed': {
            const [ax, ay] = along(-12);
            const [bx, by] = along(12);
            const [tx, ty] = component.kind === 'switch_open' ? across(10, -14) : [bx, by];
            return blank + lead(-20, -12) + lead(12, 20)
                + `<circle cx="${ax}" cy="${ay}" r="3" fill="#000"/><circle cx="${bx}" cy="${by}" r="3" fill="#000"/>`
                + `<line x1="${ax}" y1="${ay}" x2="${tx}" y2="${ty}" stroke="#000" stroke-width="1.8"/>`;
        }
        case 'ammeter':
            return blank + lead(-20, -13) + lead(13, 20) + meter('A');
        default:
            return blank + lead(-20, -13) + lead(13, 20) + meter('V');
    }
}

function circuit(figure) {
    const main = figure.components.filter((component) => component.branch === 0 && component.kind !== 'voltmeter');
    const sources = main.filter((component) => component.kind === 'cell' || component.kind === 'battery');
    const topComponents = main.filter((component) => !sources.includes(component));
    const branchNumbers = [...new Set(figure.components.filter((component) => component.branch > 0).map((component) => component.branch))].sort();
    const branches = branchNumbers.map((branch) => figure.components.filter((component) => component.branch === branch && component.kind !== 'voltmeter'));
    const voltmeters = figure.components.filter((component) => component.kind === 'voltmeter');

    const left = 70;
    const top = 70;
    const slot = 90;
    const topWireEnd = left + Math.max(2, topComponents.length + 1) * slot;
    const rungsTall = Math.max(1, ...branches.map((components) => components.length));
    const bottom = top + Math.max(170, (rungsTall + 1) * 70, (sources.length + 1) * 60);
    const rungXs = branches.map((_, index) => topWireEnd + index * 110);
    const right = branches.length ? rungXs[rungXs.length - 1] : topWireEnd;
    const width = right + 90;
    const height = bottom + 60;
    const positions = {};
    let body = '';

    body += line(left, top, right, top, 'stroke-width="1.6"') + line(left, bottom, right, bottom, 'stroke-width="1.6"') + line(left, top, left, bottom, 'stroke-width="1.6"');
    if (!branches.length) body += line(right, top, right, bottom, 'stroke-width="1.6"');
    rungXs.forEach((x) => { body += line(x, top, x, bottom, 'stroke-width="1.6"'); });
    if (branches.length > 1) {
        rungXs.forEach((x, index) => {
            if (index < rungXs.length - 1) body += `<circle cx="${x}" cy="${top}" r="4" fill="#000"/><circle cx="${x}" cy="${bottom}" r="4" fill="#000"/>`;
        });
    }

    const place = (component, x, y, vertical, labelSide) => {
        positions[component.label] = { x, y, vertical };
        body += circuitSymbol(component, x, y, vertical);
        if (component.label) {
            body += vertical
                ? text(x + labelSide * 26, y, component.label, { anchor: labelSide > 0 ? 'start' : 'end', size: 13, weight: 'bold' })
                : text(x, y - 26, component.label, { size: 13, weight: 'bold' });
        }
    };
    sources.forEach((component, index) => place(component, left, top + ((index + 1) * (bottom - top)) / (sources.length + 1), true, -1));
    topComponents.forEach((component, index) => place(component, left + (index + 1) * ((topWireEnd - left) / (topComponents.length + 1)), top, false, 1));
    branches.forEach((components, branchIndex) => {
        components.forEach((component, index) => place(component, rungXs[branchIndex], top + ((index + 1) * (bottom - top)) / (components.length + 1), true, 1));
    });

    // A voltmeter is drawn in parallel across the component it measures.
    for (const meter of voltmeters) {
        const target = positions[meter.across];
        if (!target) continue;
        const offset = 46;
        if (target.vertical) {
            // Opposite the component's own label: sources on the left wire are labelled on their left,
            // branch components on their right.
            const side = target.x <= left ? 1 : -1;
            const x = target.x + side * offset;
            body += line(target.x, target.y - 28, x, target.y - 28) + line(x, target.y - 28, x, target.y + 28) + line(target.x, target.y + 28, x, target.y + 28);
            body += `<circle cx="${target.x}" cy="${target.y - 28}" r="3" fill="#000"/><circle cx="${target.x}" cy="${target.y + 28}" r="3" fill="#000"/>`;
            body += circuitSymbol(meter, x, target.y, true) + text(x + side * 22, target.y, meter.label, { anchor: side > 0 ? 'start' : 'end', size: 13, weight: 'bold' });
        } else {
            const y = target.y + offset;
            body += line(target.x - 28, target.y, target.x - 28, y) + line(target.x - 28, y, target.x + 28, y) + line(target.x + 28, target.y, target.x + 28, y);
            body += `<circle cx="${target.x - 28}" cy="${target.y}" r="3" fill="#000"/><circle cx="${target.x + 28}" cy="${target.y}" r="3" fill="#000"/>`;
            body += circuitSymbol(meter, target.x, y, false) + text(target.x, y + 26, meter.label, { size: 13, weight: 'bold' });
        }
    }
    return wrap(width, height, body);
}

function foodWeb(figure) {
    const preyOf = Object.fromEntries(figure.nodes.map((node) => [node.id, []]));
    for (const edge of figure.edges) preyOf[edge.to].push(edge.from);
    const levels = {};
    const levelOf = (id) => {
        if (levels[id] === undefined) levels[id] = preyOf[id].length ? 1 + Math.max(...preyOf[id].map(levelOf)) : 0;
        return levels[id];
    };
    figure.nodes.forEach((node) => levelOf(node.id));
    const maxLevel = Math.max(...Object.values(levels));
    const rows = Array.from({ length: maxLevel + 1 }, (_, level) => figure.nodes.filter((node) => levels[node.id] === level));
    const widest = Math.max(...rows.map((row) => row.length));
    const width = Math.max(420, widest * 150);
    const rowGap = 90;
    const height = (maxLevel + 1) * rowGap + 30;
    const boxes = {};
    let body = '';

    rows.forEach((row, level) => {
        const y = height - 40 - level * rowGap;
        row.forEach((node, index) => {
            const boxWidth = Math.max(70, String(node.label).length * 7.6 + 20);
            const x = (width * (index + 1)) / (row.length + 1);
            boxes[node.id] = { x, y, boxWidth };
        });
    });
    for (const edge of figure.edges) {
        const from = boxes[edge.from];
        const to = boxes[edge.to];
        const startY = from.y - 14;
        const endY = to.y + 14;
        const angle = Math.atan2(endY - startY, to.x - from.x);
        body += line(from.x, startY, to.x - 3 * Math.cos(angle), endY - 3 * Math.sin(angle), 'stroke-width="1.5"') + arrowHead(to.x, endY, angle, 10);
    }
    for (const node of figure.nodes) {
        const { x, y, boxWidth } = boxes[node.id];
        body += `<rect x="${(x - boxWidth / 2).toFixed(1)}" y="${y - 14}" width="${boxWidth.toFixed(1)}" height="28" rx="6" fill="#fff" stroke="#000" stroke-width="1.5"/>` + text(x, y + 1, node.label, { size: 13 });
    }
    return wrap(width, height, body);
}

function labelledDiagram(figure) {
    const template = FIGURE_TEMPLATES[figure.template];
    const margin = 90;
    const width = 400 + margin * 2;
    const height = 320;
    let body = `<g transform="translate(${margin} 10)">${template.body}</g>`;
    const sides = { left: [], right: [] };
    for (const label of figure.labels) {
        const anchor = template.anchors[label.part];
        if (!anchor) continue;
        sides[anchor[0] < 200 ? 'left' : 'right'].push({ letter: String(label.letter).toUpperCase(), x: anchor[0] + margin, y: anchor[1] + 10 });
    }
    for (const [side, labels] of Object.entries(sides)) {
        labels.sort((a, b) => a.y - b.y);
        let nextFree = 18;
        for (const label of labels) {
            const y = Math.max(label.y, nextFree);
            nextFree = y + 30;
            const x = side === 'left' ? 30 : width - 30;
            const lineEnd = side === 'left' ? x + 13 : x - 13;
            body += line(lineEnd, y, label.x, label.y, 'stroke-width="1.1"') + `<circle cx="${label.x}" cy="${label.y}" r="2.4" fill="#000"/>`;
            body += `<circle cx="${x}" cy="${y}" r="13" fill="#fff" stroke="#000" stroke-width="1.5"/>` + text(x, y + 1, label.letter, { size: 14, weight: 'bold' });
        }
    }
    return wrap(width, height, body);
}

const RENDERERS = {
    number_line: numberLine,
    geometry,
    grid,
    cartesian_graph: cartesianGraph,
    bar_graph: categoryChart,
    line_graph: categoryChart,
    pie_chart: pieChart,
    fraction_bars: fractionBars,
    clock,
    circuit,
    food_web: foodWeb,
    labelled_diagram: labelledDiagram
};

// Returns { svg, width, height }, or null when the figure cannot be drawn.
// Options: notToScale, the translated "Not drawn to scale" note for geometry figures.
export function renderFigureSvg(figure, options = {}) {
    const renderer = RENDERERS[figure?.type];
    if (!renderer) return null;
    try {
        return renderer(figure, options);
    } catch {
        return null;
    }
}
